// A short, soft two-tone chime for new support-chat messages. Synthesised with
// the Web Audio API so there is no asset to ship or fetch, and every call is
// wrapped so a blocked/unsupported AudioContext never throws into the caller.

let audioContext: AudioContext | null = null;

const getContext = (): AudioContext | null => {
  try {
    if (!audioContext) {
      const Ctx =
        window.AudioContext || (window as unknown as {
          webkitAudioContext?: typeof AudioContext;
        }).webkitAudioContext;

      if (!Ctx) {
        return null;
      }

      audioContext = new Ctx();
    }

    return audioContext;
  } catch {
    return null;
  }
};

// Browsers create the AudioContext "suspended" until a user gesture. Resume it
// on the first interaction so the chime can actually play later, when a message
// arrives without any gesture. Registers once, removes itself after unlocking.
let unlockBound = false;

const bindUnlock = (): void => {
  if (unlockBound || typeof window === "undefined") {
    return;
  }
  unlockBound = true;

  const unlock = (): void => {
    const ctx = getContext();
    if (ctx && ctx.state === "suspended") {
      ctx.resume().catch(() => undefined);
    }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };

  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
};

if (typeof window !== "undefined") {
  bindUnlock();
}

const tone = (
  ctx: AudioContext,
  frequency: number,
  startOffset: number,
  duration: number
): void => {
  const now = ctx.currentTime + startOffset;
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = "sine";
  oscillator.frequency.value = frequency;

  // Quick attack, exponential decay - reads as a gentle "ding", not a beep.
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.linearRampToValueAtTime(0.14, now + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  oscillator.connect(gain);
  gain.connect(ctx.destination);

  oscillator.start(now);
  oscillator.stop(now + duration);
};

/**
 * Play the new-message chime. No-ops silently when audio is unavailable or the
 * browser has not yet unlocked the AudioContext (before any user gesture).
 */
export const playNotificationSound = (): void => {
  const ctx = getContext();

  if (!ctx) {
    return;
  }

  try {
    if (ctx.state === "suspended") {
      // Best-effort: resumes once the tab has had any user interaction.
      ctx.resume().catch(() => undefined);
    }

    tone(ctx, 880, 0, 0.16); // A5
    tone(ctx, 1174.66, 0.11, 0.22); // D6
  } catch {
    // Autoplay policy or an unsupported context - not worth surfacing.
  }
};
